import { useMemo } from "react";
import type { Person } from "@b2bportal/air-booking-api";
import { PassengerType } from "@b2bportal/core-types";
import type { Traveler } from "@b2bportal/traveler-api";
import type { PassengerSelectionComponentProps } from "../PassengerSelection";
import { usePassengerGrouping } from "./usePassengerGrouping";

type Passenger = Person | Traveler;

export interface UsePassengerSelectionReturn {
  uniquePassengers: Passenger[];
  passengersByAge: {
    [PassengerType.Adult]: Array<Passenger>;
    [PassengerType.Child]: Array<Passenger>;
    [PassengerType.InfantInSeat]: Array<Passenger>;
  };
  selectedPassengersByAge: {
    [PassengerType.Adult]: Array<Passenger>;
    [PassengerType.Child]: Array<Passenger>;
    [PassengerType.InfantInSeat]: Array<Passenger>;
  };
  validSelection: boolean;
}

export const usePassengerSelection = ({
  passengerList,
  allSelectedPassengerIds,
  searchPassengerCounts,
  tripDate,
}: Omit<
  PassengerSelectionComponentProps,
  "handleEdit" | "handleDelete" | "handleSelectPassenger"
> & {
  tripDate?: string | Date;
}): UsePassengerSelectionReturn => {
  // Generate a list of unique passengers
  const { uniquePassengers, passengersByAge } = usePassengerGrouping({
    passengerList,
    tripDate,
  });

  const selectedPassengersByAge = useMemo(() => {
    return {
      [PassengerType.Adult]: passengersByAge[PassengerType.Adult].filter(
        (passenger) => allSelectedPassengerIds.includes(passenger.id)
      ),
      [PassengerType.Child]: passengersByAge[PassengerType.Child].filter(
        (passenger) => allSelectedPassengerIds.includes(passenger.id)
      ),
      // this contains both infants in seat and infants on lap, as we need this before infant seat selection
      [PassengerType.InfantInSeat]: passengersByAge[
        PassengerType.InfantInSeat
      ].filter((passenger) => allSelectedPassengerIds.includes(passenger.id)),
    };
  }, [allSelectedPassengerIds, passengersByAge]);

  const validSelection = useMemo(() => {
    return (
      searchPassengerCounts.adultsCount ===
        selectedPassengersByAge[PassengerType.Adult].length &&
      searchPassengerCounts.childrenCount ===
        selectedPassengersByAge[PassengerType.Child].length &&
      selectedPassengersByAge[PassengerType.InfantInSeat].length ===
        searchPassengerCounts.infantsInSeatCount +
          searchPassengerCounts.infantsOnLapCount
    );
  }, [selectedPassengersByAge, searchPassengerCounts]);

  return {
    uniquePassengers,
    passengersByAge,
    selectedPassengersByAge,
    validSelection,
  };
};
